/**
 * Getting a licence for a protected signage item (PLAYER-6).
 *
 * ## Why the timeline cannot carry this
 *
 * A Verimatrix token lives five minutes; a timeline is cached for six hours and
 * re-read every thirty seconds. Embedding a licence URL in the timeline would
 * hand every panel a credential that is stale before the loop's second lap -
 * and would put a live credential into a document the CMS also renders in a
 * browser. So the timeline carries the `contentAssetId` and the wall asks for a
 * licence when the item's turn comes.
 *
 * ## Why it is cached here, briefly
 *
 * A one-item loop reaches the same asset every lap, and a lap can be ten
 * seconds. Asking the server for a fresh token on every restart would be a
 * request every ten seconds per panel, for a token that is still valid. So a
 * resolution is reused until shortly before it expires - long enough to be
 * cheap, short enough that the player never presents a token the licence server
 * has begun refusing.
 *
 * ## Failure is a refusal to play, never a fallback
 *
 * If a licence cannot be had, the caller must not quietly play something else.
 * `storefront-playback` refuses rather than serving an unlicensed clear
 * rendition, and this side of the wire keeps that promise: null means "do not
 * play this", and the surface skips the item.
 */

/**
 * What one resolution answered.
 *
 * A refusal is RETURNED rather than logged, because the caller is the only
 * thing that can show it. The first version returned a bare `null` and printed
 * the reason to a console nobody on a wall reads - so a licence the server
 * declined to mint rendered as a black rectangle with no account of itself,
 * which is the same swallowing that made a Shaka failure look like a teardown.
 */
export interface ItemPlaybackResult {
  playback: ResolvedItemPlayback | null;
  /** The server's own refusal code, when it gave one. */
  refusal: string | null;
}

export interface ResolvedItemPlayback {
  /** The manifest to load. */
  url: string;
  encrypted: boolean;
  /** Widevine licence URL, token included. Null for clear content. */
  licenceUrl: string | null;
  /**
   * PlayReady licence URL for the SAME stream. Null for clear content.
   *
   * Carried beside Widevine rather than instead of it: which CDM a panel ships
   * is a property of the panel, and the player offers both so the device can
   * pick. An older server that does not send this field leaves it null, which
   * is exactly the behaviour Foyer had before.
   */
  playreadyLicenceUrl: string | null;
}

interface CacheEntry {
  value: ResolvedItemPlayback;
  /** When this stops being safe to reuse (ms epoch). */
  goodUntil: number;
}

/**
 * How early to discard a resolution.
 *
 * A token is minted with a five-minute life and spends some of it in flight and
 * in a decoder's setup. Re-resolving a minute before expiry means the player
 * never opens a stream with a token the licence server is about to refuse -
 * which would surface as a black rectangle rather than as anything nameable.
 */
const EXPIRY_MARGIN_MS = 60_000;

/** Ceiling for a clear item, which carries no expiry of its own to key on. */
const CLEAR_TTL_MS = 5 * 60_000;

const cache = new Map<string, CacheEntry>();
/** In-flight requests, so one slow lap cannot start a second identical fetch. */
const inFlight = new Map<string, Promise<ItemPlaybackResult>>();

function now(): number {
  return Date.now();
}

/**
 * Resolve one catalogue item into a URL and, if it is protected, a licence.
 *
 * `null` means this item cannot be played right now - refused, unreachable, or
 * an environment that cannot mint. The caller skips it.
 */
/**
 * Who is asking, and therefore which route answers.
 *
 * Both mint the same licence from the same resolver; they differ only in what
 * they authenticate. The preview lane exists because the screen token was an
 * authentication choice standing in for a rule, never a property of DRM: a
 * licence is minted by the server and consumed by a browser's CDM, and the
 * browser the CMS preview runs in has one.
 */
export type PlaybackCaller =
  /** A panel, holding the screen token minted when it was paired. */
  | { kind: "screen"; screenId: string; token: string }
  /** The CMS preview, authenticated as the operator by the session cookie. */
  | { kind: "operator"; premiseId: string };

function requestFor(
  caller: PlaybackCaller,
  apiBase: string,
  contentAssetId: string
): { url: string; init: RequestInit } {
  const id = encodeURIComponent(contentAssetId);
  if (caller.kind === "screen") {
    return {
      url: `${apiBase}/api/signage/v1/screens/${encodeURIComponent(
        caller.screenId
      )}/play/${id}`,
      init: { headers: { "X-Screen-Token": caller.token } },
    };
  }
  return {
    url: `${apiBase}/api/cms/v1/premise/${encodeURIComponent(
      caller.premiseId
    )}/signage/play/${id}`,
    // The CMS session rides a cookie on the shared `.nenda.com` domain. It has
    // to be asked for explicitly: the panel path sends a header and needs no
    // credentials, so the default would silently send none here either.
    init: { credentials: "include" },
  };
}

export async function resolveItemPlayback(
  args: {
    apiBase: string;
    caller: PlaybackCaller;
    contentAssetId: string;
  },
  fetchImpl: typeof fetch = fetch
): Promise<ItemPlaybackResult> {
  // The caller is part of the key: an operator and a panel get DIFFERENT
  // tokens, and a cached resolution from one must never be handed to the other.
  const who =
    args.caller.kind === "screen"
      ? args.caller.screenId
      : `premise:${args.caller.premiseId}`;
  const key = `${who}:${args.contentAssetId}`;
  const hit = cache.get(key);
  if (hit && hit.goodUntil > now())
    return { playback: hit.value, refusal: null };

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = (async (): Promise<ItemPlaybackResult> => {
    try {
      const { url, init } = requestFor(
        args.caller,
        args.apiBase,
        args.contentAssetId
      );
      const res = await fetchImpl(url, init);
      if (!res.ok) {
        // A status, not a silence. `404 content_not_scheduled` is the preview
        // route saying "you have not put this on a wall yet", which is a very
        // different conversation from a licence the server declined to mint.
        const detail = await refusalOf(res);
        const code = detail ?? `HTTP ${res.status}`;
        console.warn(
          `[signage-licence] refused ${args.contentAssetId}: ${code}`
        );
        return { playback: null, refusal: code };
      }
      const body = (await res.json()) as {
        playable?: {
          url?: string;
          encrypted?: boolean;
          licenceUrl?: string | null;
          playreadyLicenceUrl?: string | null;
          licenceExpiresAt?: string | null;
        };
        refusal?: { code?: string; detail?: string };
      };
      if (!body.playable?.url) {
        // The server said why; say it once so a log capture carries it, AND
        // hand it back so a surface can render it. A refusal is a
        // configuration or catalogue fact, not a panel fault. Detail first,
        // for the same reason as `refusalOf`.
        const code =
          body.refusal?.detail ?? body.refusal?.code ?? "no playable url";
        console.warn(
          `[signage-licence] refused ${args.contentAssetId}: ${code}`
        );
        return { playback: null, refusal: code };
      }
      const value: ResolvedItemPlayback = {
        url: body.playable.url,
        encrypted: body.playable.encrypted === true,
        licenceUrl: body.playable.licenceUrl ?? null,
        playreadyLicenceUrl: body.playable.playreadyLicenceUrl ?? null,
      };
      // Key the cache to the TOKEN's own expiry where there is one, rather than
      // to a constant of ours: the server owns the TTL and may change it, and a
      // local guess that outlives the token is a black screen.
      const expiresAt = body.playable.licenceExpiresAt
        ? Date.parse(body.playable.licenceExpiresAt)
        : NaN;
      const goodUntil = Number.isFinite(expiresAt)
        ? expiresAt - EXPIRY_MARGIN_MS
        : now() + CLEAR_TTL_MS;
      // A token that is already inside the margin is used once and not stored;
      // caching it would guarantee the next lap starts with a dead one.
      if (goodUntil > now()) cache.set(key, { value, goodUntil });
      return { playback: value, refusal: null };
    } catch (err) {
      // Offline, or the API is down. The surface keeps playing what it has,
      // and this is NOT reported as a refusal: a refusal is a decision, and
      // an unreachable server has not made one.
      //
      // Silent to the RETURN VALUE on purpose (see above) - but silent to the
      // console too was a real gap, not a design choice: 2026-09-08,
      // hasselbacken/301, a Philips MediaSuite got stuck here permanently
      // (not a transient blip) with nothing to show for it anywhere - no
      // banner (correctly, per the comment above), but also no log line for
      // a `log_capture` to ever have carried. This is a "still trying, here
      // is why" line, not a refusal.
      console.warn(
        `[signage-licence] fetch failed for ${args.contentAssetId} (offline or the API is down - not a refusal): ${String(err).slice(0, 200)}`
      );
      return { playback: null, refusal: null };
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}

/**
 * The server's own refusal, if the body carries one. Never throws.
 *
 * Prefers `detail` over `code` where there is one. `no_licence_available` is a
 * BUCKET - a malformed signing key, a missing one, a mint that threw - and the
 * detail is the only part that says which. Reading the code alone threw the
 * sentence away and left an operator holding a bucket name, which is the same
 * lossiness this file has been fixed for twice already.
 */
async function refusalOf(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as {
      error?: string;
      refusal?: { code?: string; detail?: string };
    };
    return body?.refusal?.detail || body?.refusal?.code || body?.error || null;
  } catch {
    return null;
  }
}

/** Test seam, and what a role change should call. */
export function resetPlaybackLicences(): void {
  cache.clear();
  inFlight.clear();
}
